import { Observable } from "../../Misc/observable";
/**
 * This class represents a single component (for example button or thumbstick) of a motion controller
 */
var WebXRControllerComponent = /** @class */ (function () {
    /**
     * Creates a new component for a motion controller.
     * It is created by the motion controller itself
     *
     * @param id the id of this component
     * @param type the type of the component
     * @param _buttonIndex index in the buttons array of the gamepad
     * @param _axesIndices indices of the values in the axes array of the gamepad
     */
    function WebXRControllerComponent(
    /**
     * the id of this component
     */
    id, 
    /**
     * the type of the component
     */
    type, _buttonIndex, _axesIndices) {
        if (_buttonIndex === void 0) { _buttonIndex = -1; }
        if (_axesIndices === void 0) { _axesIndices = []; }
        this.id = id;
        this.type = type;
        this._buttonIndex = _buttonIndex;
        this._axesIndices = _axesIndices;
        this._axes = {
            x: 0,
            y: 0,
        };
        this._changes = {};
        this._currentValue = 0;
        this._hasChanges = false;
        this._pressed = false;
        this._touched = false;
        /**
         * If axes are available for this component (like a touchpad or thumbstick) the observers will be notified when
         * the axes data changes
         */
        this.onAxisValueChangedObservable = new Observable();
        /**
         * Observers registered here will be triggered when the state of a button changes
         * State change is either pressed / touched / value
         */
        this.onButtonStateChangedObservable = new Observable();
    }
    Object.defineProperty(WebXRControllerComponent.prototype, "axes", {
        /**
         * The current axes data. If this component has no axes it will still return an object { x: 0, y: 0 }
         */
        get: function () {
            return this._axes;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WebXRControllerComponent.prototype, "changes", {
        /**
         * Get the changes. Elements will be populated only if they changed with their previous and current value
         */
        get: function () {
            return this._changes;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WebXRControllerComponent.prototype, "hasChanges", {
        /**
         * Return whether or not the component changed the last frame
         */
        get: function () {
            return this._hasChanges;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WebXRControllerComponent.prototype, "pressed", {
        /**
         * is the button currently pressed
         */
        get: function () {
            return this._pressed;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WebXRControllerComponent.prototype, "touched", {
        /**
         * is the button currently touched
         */
        get: function () {
            return this._touched;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WebXRControllerComponent.prototype, "value", {
        /**
         * Get the current value of this component
         */
        get: function () {
            return this._currentValue;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Dispose this component
     */
    WebXRControllerComponent.prototype.dispose = function () {
        this.onAxisValueChangedObservable.clear();
        this.onButtonStateChangedObservable.clear();
    };
    /**
     * Are there axes correlating to this component
     * @return true is axes data is available
     */
    WebXRControllerComponent.prototype.isAxes = function () {
        return this._axesIndices.length !== 0;
    };
    /**
     * Is this component a button (hence - pressable)
     * @returns true if can be pressed
     */
    WebXRControllerComponent.prototype.isButton = function () {
        return this._buttonIndex !== -1;
    };
    /**
     * update this component using the gamepad object it is in. Called on every frame
     * @param nativeController the native gamepad controller object
     */
    WebXRControllerComponent.prototype.update = function (nativeController) {
        var buttonUpdated = false;
        var axesUpdate = false;
        this._hasChanges = false;
        this._changes = {};
        if (this.isButton()) {
            var button = nativeController.buttons[this._buttonIndex];
            // defensive, in case a profile was forced
            if (!button) {
                return;
            }
            if (this._currentValue !== button.value) {
                this.changes.value = {
                    current: button.value,
                    previous: this._currentValue,
                };
                buttonUpdated = true;
                this._currentValue = button.value;
            }
            if (this._touched !== button.touched) {
                this.changes.touched = {
                    current: button.touched,
                    previous: this._touched,
                };
                buttonUpdated = true;
                this._touched = button.touched;
            }
            if (this._pressed !== button.pressed) {
                this.changes.pressed = {
                    current: button.pressed,
                    previous: this._pressed,
                };
                buttonUpdated = true;
                this._pressed = button.pressed;
            }
        }
        if (this.isAxes()) {
            if (this._axes.x !== nativeController.axes[this._axesIndices[0]]) {
                this.changes.axes = {
                    current: {
                        x: nativeController.axes[this._axesIndices[0]],
                        y: this._axes.y,
                    },
                    previous: {
                        x: this._axes.x,
                        y: this._axes.y,
                    },
                };
                this._axes.x = nativeController.axes[this._axesIndices[0]];
                axesUpdate = true;
            }
            if (this._axes.y !== nativeController.axes[this._axesIndices[1]]) {
                if (this.changes.axes) {
                    this.changes.axes.current.y = nativeController.axes[this._axesIndices[1]];
                }
                else {
                    this.changes.axes = {
                        current: {
                            x: this._axes.x,
                            y: nativeController.axes[this._axesIndices[1]],
                        },
                        previous: {
                            x: this._axes.x,
                            y: this._axes.y,
                        },
                    };
                }
                this._axes.y = nativeController.axes[this._axesIndices[1]];
                axesUpdate = true;
            }
        }
        if (buttonUpdated) {
            this._hasChanges = true;
            this.onButtonStateChangedObservable.notifyObservers(this);
        }
        if (axesUpdate) {
            this._hasChanges = true;
            this.onAxisValueChangedObservable.notifyObservers(this._axes);
        }
    };
    /**
     * button component type
     */
    WebXRControllerComponent.BUTTON_TYPE = "button";
    /**
     * squeeze component type
     */
    WebXRControllerComponent.SQUEEZE_TYPE = "squeeze";
    /**
     * Thumbstick component type
     */
    WebXRControllerComponent.THUMBSTICK_TYPE = "thumbstick";
    /**
     * Touchpad component type
     */
    WebXRControllerComponent.TOUCHPAD_TYPE = "touchpad";
    /**
     * trigger component type
     */
    WebXRControllerComponent.TRIGGER_TYPE = "trigger";
    return WebXRControllerComponent;
}());
export { WebXRControllerComponent };
//# sourceMappingURL=webXRControllerComponent.js.map